import type { CSSProperties, JSX } from 'react';
import { hexWithAlpha, type Palette } from '../../../_theme/palette.js';
import type { SurveyTheme } from '../types.js';

interface BrandHeaderProps {
  palette: Palette;
  theme: SurveyTheme | undefined;
  hideBranding?: boolean;
}

// Survey brand row that sits at the top of the survey card, between the
// progress bar and the survey content. Mirrors the main-app placement so
// embedded SDK and the public hosted page look the same.
// - `branding.showLogo: false` hides the logo
// - empty/missing brandName + missing logo = component renders nothing
// - `appearance.hideBranding` removes it entirely
export function BrandHeader({ palette, theme, hideBranding }: BrandHeaderProps): JSX.Element | null {
  if (hideBranding) return null;
  const brandName = theme?.branding?.brandName?.trim();
  const showLogo = theme?.branding?.showLogo !== false && Boolean(theme?.branding?.logoUrl);
  if (!showLogo && !brandName) return null;

  return (
    <div style={containerStyle(palette)}>
      {showLogo && (
        <span style={logoFrameStyle(palette)}>
          <img
            src={theme!.branding!.logoUrl!}
            alt={brandName || ''}
            aria-hidden={brandName ? undefined : 'true'}
            style={logoStyle()}
          />
        </span>
      )}
      {brandName && <span style={brandNameStyle(palette)}>{brandName}</span>}
    </div>
  );
}

function containerStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 24px 14px',
    borderBottom: `1px solid ${hexWithAlpha(palette.border, 0.6)}`,
    flexShrink: 0,
  };
}

function logoFrameStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
    borderRadius: 8,
    backgroundColor: hexWithAlpha(palette.textMuted, 0.06),
    border: `1px solid ${hexWithAlpha(palette.border, 0.5)}`,
    overflow: 'hidden',
    flex: '0 0 auto',
  };
}

function logoStyle(): CSSProperties {
  return {
    height: 22,
    width: 22,
    maxHeight: 22,
    maxWidth: 22,
    display: 'block',
    objectFit: 'contain',
  };
}

function brandNameStyle(palette: Palette): CSSProperties {
  return {
    fontSize: 15,
    fontWeight: 600,
    color: palette.text,
    letterSpacing: '-0.01em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
}
